import JSZip from "jszip";
import { ItemPlanilha, TipoItem, Planilha } from "@models/index";
import { faciteExportService } from "./faciteExcelJSExportService";
import { excelExportService, TotaisPlanilha } from "./excelExportService";
import { planilhaService } from "./planilhaService";
import apiClient from "./api";
import {
  baixarArquivo,
  sanitizarNomeArquivo,
} from "../utils/nome-arquivo";
import { compararCotacoesPorPreco } from "../utils/planilhaItemUtils";

interface FornecedorComPdf {
  nome: string;
  pdfUrl: string;
  /** 1 = vencedor. Prefixa o nome do arquivo para preservar a ordem na pasta. */
  posicao: number;
}


export const exportacaoUnificadaService = {
  /**
   * Gera um arquivo ZIP com:
   * 1. Excel do FACITE
   * 2. Excel de exportação normal
   * 3. Pastas com PDFs dos fornecedores organizadas por item
   */
  gerarPacoteCompleto: async (
    planilha: Planilha,
    rowData: ItemPlanilha[],
    totais: TotaisPlanilha,
  ) => {
    try {
      // Criar instância do JSZip
      const zip = new JSZip();

      // 1. Gerar Excel do FACITE e adicionar ao ZIP
      let faciteGerado = false;
      try {
        // Buscar planilha com dados completos dos fornecedores
        const planilhaCompleta =
          await planilhaService.getPlanilhaComFornecedoresCompletos(
            planilha.id,
          );

        const faciteBuffer = await faciteExportService.generateFaciteBuffer(
          planilhaCompleta,
          rowData,
          totais,
        );
        if (faciteBuffer) {
          const nomeEvento = planilha?.evento || "Evento";
          const dataAtual = new Date()
            .toLocaleDateString("pt-BR")
            .replace(/\//g, "-");
          zip.file(
            `${sanitizeFileName(`FACITE_${nomeEvento}_${dataAtual}`)}.xlsx`,
            faciteBuffer,
          );
          faciteGerado = true;
        }
      } catch (error: any) {
        console.error("Erro ao gerar FACITE:", error);
        console.error("Detalhes do erro:", error.message, error.stack);
        // Continua mesmo se o FACITE falhar
      }

      // 2. Gerar Excel de exportação normal e adicionar ao ZIP
      let excelGerado = false;
      try {
        const exportBuffer = excelExportService.generateExcelBuffer(
          planilha,
          rowData,
          totais,
        );
        if (exportBuffer) {
          const nomeEvento = planilha?.evento || "Evento";
          const dataAtual = new Date()
            .toLocaleDateString("pt-BR")
            .replace(/\//g, "-");
          zip.file(
            `${sanitizeFileName(`Planilha_${nomeEvento}_${dataAtual}`)}.xlsx`,
            exportBuffer,
          );
          excelGerado = true;
        } else {
          console.warn("Excel buffer está vazio");
        }
      } catch (error: any) {
        console.error("Erro ao gerar Excel de exportação:", error);
        console.error("Detalhes do erro:", error.message, error.stack);
        // Continua mesmo se o Excel falhar
      }

      // 3. Adicionar pastas com PDFs dos fornecedores
      const totalPdfs = await adicionarPastasPdfs(zip, planilha, rowData);

      // Gerar arquivo ZIP
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Nome do arquivo ZIP: "NomeDaPlanilha.zip"
      const nomeEvento = planilha?.evento || "Planilha";
      baixarArquivo(zipBlob, nomeEvento, "zip");

      // Montar mensagem de sucesso
      const itensGerados = [];
      if (faciteGerado) itensGerados.push("FACITE");
      if (excelGerado) itensGerados.push("Planilha");
      if (totalPdfs > 0) itensGerados.push(`${totalPdfs} PDF(s)`);

      const mensagem =
        itensGerados.length > 0
          ? `Pacote gerado com: ${itensGerados.join(", ")}`
          : "Pacote gerado";

      return {
        success: true,
        totalPdfs,
        faciteGerado,
        excelGerado,
        message: mensagem,
      };
    } catch (error) {
      console.error("Erro ao gerar pacote completo:", error);
      throw error;
    }
  },
};

/**
 * Adiciona pastas com PDFs dos fornecedores ao ZIP
 */
async function adicionarPastasPdfs(
  zip: JSZip,
  planilha: Planilha,
  rowData: ItemPlanilha[],
): Promise<number> {
  try {
    // Filtrar apenas itens amarelos do ITEM 2
    console.log(planilha);
    const itensItem2 = rowData.filter(
      (item) =>
        (item.tipo === TipoItem.ITEM ||
          item.tipo === TipoItem.ITEM_TAXAS ||
          item.tipo === TipoItem.ITEM_TRANSPORTE) &&
        item.numero &&
        item.numero.startsWith("2."),
    );
    if (itensItem2.length === 0) {
      console.warn("Nenhum item encontrado no ITEM 2 para PDFs");
      return 0;
    }

    // Contador de PDFs baixados
    let totalPdfs = 0;

    // Processar cada item amarelo
    for (const itemAmarelo of itensItem2) {
      // Nome da pasta: "2.1 - (Nome do PRODUTOS E SERVIÇOS)"
      const nomePasta = `${itemAmarelo.numero} - ${sanitizeFolderName(
        itemAmarelo.item,
      )}`;

      // Taxas e seguros: o anexo pertence à PRÓPRIA linha amarela.
      //
      // A versão anterior varria as linhas brancas (DESCRITIVO_TAXAS) atrás do
      // arquivo, mas o upload só é permitido em item_taxas — o backend recusa
      // qualquer outro tipo. Aquelas chamadas retornavam 404 sempre e o 404 era
      // tratado como "sem arquivo", então nenhum seguro entrava no pack.
      if (itemAmarelo.tipo === TipoItem.ITEM_TAXAS) {
        if (!itemAmarelo.id) {
          // Linha ainda não salva no banco: não há como ter anexo.
          continue;
        }

        try {
          const response = await apiClient.get(`/itens/${itemAmarelo.id}/arquivo`);

          if (response.data.success && response.data.data) {
            const arquivoInfo = response.data.data;
            const arquivoBlob = await downloadPdfFromUrl(arquivoInfo.url);

            const pasta = zip.folder(nomePasta);
            if (!pasta) {
              console.warn(`Não foi possível criar pasta para ${nomePasta}`);
              continue;
            }

            pasta.file(sanitizeFileName(arquivoInfo.nomeArquivo), arquivoBlob);
            totalPdfs++;
          }
        } catch (error: any) {
          // 404 é esperado quando a taxa não tem anexo.
          if (error.response?.status !== 404) {
            console.error(
              `Erro ao baixar anexo da taxa ${itemAmarelo.numero}:`,
              error,
            );
          }
        }

        continue; // Pular lógica de fornecedores para ITEM_TAXAS
      }

      // Transportes: o anexo é de CADA fornecedor, e mora na linha branca.
      //
      // Diferente de taxas, onde o documento é do item inteiro e fica na linha
      // amarela. Sem este ramo o bloco não gerava pasta nenhuma: o filtro acima
      // nem incluía ITEM_TRANSPORTE, e mesmo incluído a busca do anexo cairia
      // na linha amarela, que não tem arquivo.
      if (itemAmarelo.tipo === TipoItem.ITEM_TRANSPORTE) {
        const inicio = rowData.indexOf(itemAmarelo);
        const linhasDoItem: ItemPlanilha[] = [];
        for (let i = inicio + 1; i < rowData.length; i++) {
          if (rowData[i].tipo !== TipoItem.DESCRITIVO_TRANSPORTE) break;
          linhasDoItem.push(rowData[i]);
        }

        // Mesma ordem das outras pastas: menor preço primeiro. O Explorer
        // ordena alfabeticamente, então a posição vai no nome do arquivo.
        const ordenadas = [...linhasDoItem].sort(compararCotacoesPorPreco);

        // A pasta só nasce quando houver o primeiro anexo, para não deixar
        // pasta vazia no pacote de quem ainda não anexou nada.
        let pasta: JSZip | null = null;

        for (const [indice, linha] of ordenadas.entries()) {
          if (!linha.id) continue; // linha ainda não salva não tem anexo

          try {
            const response = await apiClient.get(`/itens/${linha.id}/arquivo`);
            if (!response.data.success || !response.data.data) continue;

            const arquivoInfo = response.data.data;
            const arquivoBlob = await downloadPdfFromUrl(arquivoInfo.url);

            pasta = pasta ?? zip.folder(nomePasta);
            if (!pasta) {
              console.warn(`Não foi possível criar pasta para ${nomePasta}`);
              break;
            }

            // Mantém a extensão original: aqui entra PDF, imagem e planilha,
            // e renomear tudo para .pdf deixaria o arquivo sem abrir.
            const nomeOriginal: string = arquivoInfo.nomeArquivo || "";
            const ponto = nomeOriginal.lastIndexOf(".");
            const extensao = ponto > 0 ? nomeOriginal.slice(ponto) : "";

            pasta.file(
              `${indice + 1} - ${sanitizeFileName(
                linha.item || "Fornecedor",
              )}${extensao}`,
              arquivoBlob,
            );
            totalPdfs++;
          } catch (error: any) {
            // 404 é esperado na linha sem anexo.
            if (error.response?.status !== 404) {
              console.error(
                `Erro ao baixar anexo do transporte ${linha.numero}:`,
                error,
              );
            }
          }
        }

        continue; // Pular lógica de fornecedores para ITEM_TRANSPORTE
      }

      // Lógica existente para ITEM normal (com fornecedores)
      // Filtrar fornecedores que possuem PDF
      const fornecedoresComPdf: FornecedorComPdf[] = (
        itemAmarelo.fornecedores || []
      )
        .filter((f) => f.orcamento?.pdfUrl || f.orcamentoPdfUrl)
        // Mesma ordenação das linhas da planilha: menor preço primeiro, e valor
        // zero ou ausente por último. Sem isso a pasta saía na ordem de cadastro
        // e não batia com a ordem que o usuário vê na tela.
        .sort(compararCotacoesPorPreco)
        .map((f, indice) => ({
          nome: f.fornecedor?.nome || f.fornecedorNome || "Fornecedor",
          pdfUrl: f.orcamento?.pdfUrl || f.orcamentoPdfUrl!,
          posicao: indice + 1,
        }));

      // Se não houver PDFs neste item, pular
      if (fornecedoresComPdf.length === 0) {
        continue;
      }

      // Criar pasta no ZIP
      const pasta = zip.folder(nomePasta);

      if (!pasta) {
        console.warn(`Não foi possível criar pasta para ${nomePasta}`);
        continue;
      }

      // Baixar e adicionar cada PDF à pasta
      for (const fornecedor of fornecedoresComPdf) {
        try {
          // Baixar PDF
          const pdfBlob = await downloadPdfFromUrl(fornecedor.pdfUrl);

          // Prefixo com a posição: o Explorer ordena a pasta alfabeticamente, e
          // sem ele a ordem por preço se perderia na hora de visualizar.
          const nomeArquivo = `${fornecedor.posicao} - ${sanitizeFileName(
            fornecedor.nome,
          )}.pdf`;

          // Adicionar PDF à pasta
          pasta.file(nomeArquivo, pdfBlob);
          totalPdfs++;
        } catch (error) {
          console.error(
            `Erro ao baixar PDF do fornecedor ${fornecedor.nome}:`,
            error,
          );
          // Continuar com os demais PDFs mesmo se um falhar
        }
      }
    }

    return totalPdfs;
  } catch (error) {
    console.error("Erro ao adicionar pastas de PDFs:", error);
    return 0;
  }
}

/**
 * Baixa um PDF de uma URL e retorna como Blob
 */
async function downloadPdfFromUrl(url: string): Promise<Blob> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Erro ao baixar PDF: ${response.statusText}`);
  }

  return await response.blob();
}

/**
 * Remove caracteres inválidos para nomes de pasta
 */
function sanitizeFolderName(name: string): string {
  return sanitizarNomeArquivo(name, 200);
}

/**
 * Remove caracteres inválidos para nomes de arquivo
 */
function sanitizeFileName(name: string): string {
  return sanitizarNomeArquivo(name, 200).replace(/\s+/g, "_");
}
